import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWasteGeneratedList,
  addWasteGeneratedToBusiness,
  updateWasteGenerated,
  deleteWasteGenerated,
} from "../../../../../../apis/Business/WasteGeneratedApi";
import { getProcessingFinalTreatmentDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const WasteGeneratedTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getWasteGeneratedList({
      businessId,
      setObjects: setData,
    });
    getProcessingFinalTreatmentDropdown({ setDropdown: setDropdownProcess });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateWasteGenerated({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        final_intermediate_method: editedItem["final_intermediate_method.value"]
          ? editedItem["final_intermediate_method.value"].value
          : editedItem["final_intermediate_method"]
          ? editedItem["final_intermediate_method"].value
          : null,
        sold_intermediate_product: editedItem.sold_intermediate_product,
        qty: editedItem.qty,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addWasteGeneratedToBusiness({
        businessId,
        ...newItem,
        final_intermediate_method: newItem["final_intermediate_method.value"]
          ? newItem["final_intermediate_method.value"].value
          : null,
        sold_intermediate_product: newItem.sold_intermediate_product,
        qty: newItem.qty,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWasteGenerated({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "sold_intermediate_product",
        header: t("WASTE"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "final_intermediate_method.value",
        header: `${t("Final treatment method")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "130px",
        isSubgroup: true,
      },
      {
        accessor: "qty",
        header: `${t("QUANTITY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "final_intermediate_method.value": dropdownProcess,
    }),
    [dropdownProcess]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default WasteGeneratedTable;
